/**
 * @description Slide-over detail view for a recurring plan.
 * Follows EmployeeSlideout.js Headless UI pattern (Dialog + Transition).
 *
 * Key features:
 * - Headless UI Dialog with translate-x slide animation
 * - Plan summary: customer, amount, frequency, status, card
 * - Line Items + Discounts (`Stacked (10% + 10%)` or `10%`) when product snapshots exist
 * - Paused payment-method banner with Update → Edit Plan
 * - Upcoming charges: active preview, or paused open periods as (Failed)
 *   plus the next future cadence date
 * - Charge history (5 per page) with a subtle prev/next pager
 * - Actions: Edit, Pause, Resume (dedicated dialog), Cancel
 *
 * @dependencies
 * - @headlessui/react: Dialog, Transition
 * - recurringPlanService: API client
 * - ConfirmActionModal: Shared confirmation dialog (pause / cancel only)
 * - ResumeRecurringPlanModal: Continue vs Continue + bill missed
 * - EditRecurringPlanModal: Plan edit form
 *
 * @notes
 * - Nested Dialogs MUST render inside this parent Dialog (Headless UI stack).
 * - Open periods come from the plan DTO (next_charge_date + cadence), not
 *   charge history alone.
 * - Coerce open-period amountCents with Number() before summing (pg may send strings).
 */

import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Calendar, CreditCard, Clock, CheckCircle, XCircle, AlertTriangle, Loader, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import recurringPlanService from '../../services/api/recurringPlanService';
import { formatCurrency } from '../../utils/invoiceHelpers';
import formatChargeDate from '../../utils/formatChargeDate';
import formatChargeHistoryError from '../../utils/formatChargeHistoryError';
import formatStackedDiscounts from '../../utils/formatStackedDiscounts';
import { getCustomerDisplayName } from '../../utils/customerHelpers';
import StatusBadge from '../shared/StatusBadge';
import PermissionGate from '../shared/PermissionGate';
import { PERMISSIONS } from '../../constants/permissionMappings';
import EditRecurringPlanModal from './EditRecurringPlanModal';
import ResumeRecurringPlanModal from './ResumeRecurringPlanModal';
import ConfirmActionModal from '../shared/ConfirmActionModal';

const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

const CHARGE_STATUS_ICONS = {
  success: <CheckCircle className="w-4 h-4 text-green-500" />,
  failed: <XCircle className="w-4 h-4 text-red-500" />,
  skipped: <AlertTriangle className="w-4 h-4 text-yellow-500" />,
  processing: <Loader className="w-4 h-4 text-blue-500 animate-spin" />,
  pending: <Clock className="w-4 h-4 text-gray-400" />
};

const HISTORY_PAGE_SIZE = 5;

const PAUSE_REASON_LABELS = {
  CARD_EXPIRED: 'card expired',
  CARD_INACTIVE: 'card inactive',
  STORED_CARD_NOT_FOUND: 'card not found'
};

/**
 * Paused upcoming list: owed open periods marked Failed, plus the next
 * future cadence date (unmarked) so the operator sees the restored schedule.
 * @param {object} plan
 * @returns {Array<{ date: string, amountCents: number, failed: boolean }>}
 */
function buildPausedUpcoming(plan) {
  const rows = (plan.openPeriods || []).map((period) => ({
    date: period.date,
    amountCents: period.amountCents || plan.amount_cents,
    failed: true
  }));
  if (
    plan.nextFutureChargeDate
    && !rows.some((row) => row.date === plan.nextFutureChargeDate)
  ) {
    rows.push({
      date: plan.nextFutureChargeDate,
      amountCents: plan.amount_cents,
      failed: false
    });
  }
  return rows;
}

export default function RecurringPlanDetail({ planId, onClose, onPlanUpdated }) {
  const [plan, setPlan] = useState(null);
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, action: null });

  const isOpen = !!planId;

  const applyPlan = (nextPlan, dates) => {
    setPlan(nextPlan);
    setHistoryRows(nextPlan.chargeHistory || []);
    setHistoryTotal(nextPlan.chargeHistoryTotalCount || 0);
    setHistoryPage(1);
    if (Array.isArray(dates)) {
      setUpcomingDates(dates);
    }
  };

  const reloadPlan = async ({ closeSlideover = false } = {}) => {
    const [planResult, datesResult] = await Promise.all([
      recurringPlanService.getPlan(planId),
      recurringPlanService.getUpcomingDates(planId, 5).catch(() => ({ dates: [] }))
    ]);
    applyPlan(planResult.plan, datesResult.dates || []);
    onPlanUpdated?.();
    if (closeSlideover) onClose();
  };

  useEffect(() => {
    if (!planId) return;
    const loadPlan = async () => {
      try {
        setLoading(true);
        const [planResult, datesResult] = await Promise.all([
          recurringPlanService.getPlan(planId),
          recurringPlanService.getUpcomingDates(planId, 5).catch(() => ({ dates: [] }))
        ]);
        applyPlan(planResult.plan, datesResult.dates || []);
      } catch (err) {
        console.error('Failed to load plan details:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPlan();
  }, [planId]);

  const handlePause = () => {
    setConfirmModal({
      open: true, action: 'pause',
      title: 'Pause Recurring Plan',
      message: 'Pause this plan? No future charges will be processed until resumed.',
      confirmText: 'Pause Plan',
      variant: 'warning'
    });
  };

  const handleCancel = () => {
    setConfirmModal({
      open: true, action: 'cancel',
      title: 'Cancel Recurring Plan',
      message: 'Cancel this plan permanently? This cannot be undone. Any scheduled charges will not be processed.',
      confirmText: 'Cancel Plan',
      variant: 'danger'
    });
  };

  const handleConfirmAction = async () => {
    const { action } = confirmModal;
    try {
      setActionLoading(true);
      if (action === 'pause') await recurringPlanService.pausePlan(planId);
      else if (action === 'cancel') await recurringPlanService.cancelPlan(planId);
      setConfirmModal({ open: false, action: null });
      onPlanUpdated();
      onClose();
    } catch (err) {
      console.error(`Failed to ${action} plan:`, err);
      setConfirmModal({ open: false, action: null });
    } finally {
      setActionLoading(false);
    }
  };

  const handleHistoryPageChange = async (nextPage) => {
    if (!planId || historyLoading) return;
    const totalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));
    if (nextPage < 1 || nextPage > totalPages || nextPage === historyPage) return;
    try {
      setHistoryLoading(true);
      const result = await recurringPlanService.getChargeHistory(planId, {
        page: nextPage,
        limit: HISTORY_PAGE_SIZE
      });
      setHistoryRows(result.chargeHistory || []);
      if (typeof result.totalCount === 'number') {
        setHistoryTotal(result.totalCount);
      }
      setHistoryPage(nextPage);
    } catch (err) {
      console.error('Failed to load charge history page:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const customerName = plan ? (getCustomerDisplayName({
    firstName: plan.customer_first_name,
    lastName: plan.customer_last_name,
    companyName: plan.customer_company_name
  }) || 'Unknown Customer') : '';

  const pausedUpcoming = plan && plan.status === 'paused' ? buildPausedUpcoming(plan) : [];
  const openTotalCents = (plan?.openPeriods || []).reduce(
    (sum, row) => sum + (Number(row.amountCents) || 0),
    0
  );
  const reasonLabel = plan?.pauseReasonCode
    ? PAUSE_REASON_LABELS[plan.pauseReasonCode] || plan.pauseReasonCode.toLowerCase().replace(/_/g, ' ')
    : null;
  const showPausedBanner = plan?.status === 'paused' && plan.cardUsable === false;
  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));
  const historyFrom = historyTotal === 0 ? 0 : ((historyPage - 1) * HISTORY_PAGE_SIZE) + 1;
  const historyTo = Math.min(historyPage * HISTORY_PAGE_SIZE, historyTotal);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-400"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                {/* Slideout Panel */}
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-out duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in duration-400"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col bg-white shadow-2xl">

                      {/* Header */}
                      <div className="bg-white px-6 py-6 border-b border-gray-200">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-lg font-semibold leading-6 text-gray-900">
                            Recurring Plan
                          </Dialog.Title>
                          <div className="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 transition-colors duration-200"
                              onClick={onClose}
                            >
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                        {plan && (
                          <p className="mt-1 text-sm text-gray-500">{customerName}</p>
                        )}
                      </div>

                      {/* Body */}
                      <div className="flex-1 overflow-y-auto">
                        {loading ? (
                          <div className="p-6">
                            <div className="animate-pulse space-y-4">
                              <div className="h-8 bg-gray-200 rounded w-48" />
                              <div className="h-4 bg-gray-200 rounded w-32" />
                              <div className="h-40 bg-gray-200 rounded" />
                            </div>
                          </div>
                        ) : !plan ? (
                          <div className="p-6 text-center text-gray-500">Plan not found</div>
                        ) : (
                          <div className="p-6 space-y-6">
                            {/* Status + Amount */}
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-3xl font-bold text-gray-900">{formatCurrency(plan.amount_cents / 100)}</p>
                                <p className="text-sm text-gray-500">
                                  {plan.interval_count > 1 ? `Every ${plan.interval_count} ` : ''}
                                  {FREQUENCY_LABELS[plan.frequency] || plan.frequency}
                                </p>
                              </div>
                              <StatusBadge value={plan.status} type="recurring_status" />
                            </div>

                            {showPausedBanner && (
                              <div className="flex items-start justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-start gap-2 text-sm text-amber-900">
                                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                  <span>
                                    Plan paused — Payment method failed
                                    {reasonLabel ? ` (${reasonLabel})` : ''}
                                  </span>
                                </div>
                                <PermissionGate permission={PERMISSIONS.recurring.edit}>
                                  <button
                                    type="button"
                                    onClick={() => setShowEditModal(true)}
                                    className="flex-shrink-0 text-sm font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700"
                                  >
                                    Update
                                  </button>
                                </PermissionGate>
                              </div>
                            )}

                            {/* Plan Details */}
                            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                              {plan.description && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-500">Description</span>
                                  <span className="text-sm font-medium text-right">{plan.description}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Start Date</span>
                                <span className="text-sm font-medium">
                                  {plan.start_date ? formatChargeDate(plan.start_date, 'MMM d, yyyy') : '—'}
                                </span>
                              </div>
                              {plan.end_date && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-500">End Date</span>
                                  <span className="text-sm font-medium">{formatChargeDate(plan.end_date, 'MMM d, yyyy')}</span>
                                </div>
                              )}
                              {plan.max_occurrences && (
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-500">Max Payments</span>
                                  <span className="text-sm font-medium">{plan.total_charges_count} / {plan.max_occurrences}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Next Charge</span>
                                <span className="text-sm font-medium">
                                  {plan.next_charge_date ? formatChargeDate(plan.next_charge_date, 'MMM d, yyyy') : '—'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Total Charged</span>
                                <span className="text-sm font-medium">
                                  {formatCurrency(plan.total_charged_cents / 100)} ({plan.total_charges_count} payments)
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-sm text-gray-500">Type</span>
                                <span className="text-sm font-medium">
                                  {plan.cof_type === 'subscription' ? 'Subscription (finite)' : 'Recurring (ongoing)'}
                                </span>
                              </div>
                            </div>

                            {(plan.items || []).length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Line Items</h4>
                                <div className="space-y-1">
                                  {plan.items.map((item) => (
                                    <div
                                      key={item.id || `${item.product_id}-${item.sort_order}`}
                                      className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm"
                                    >
                                      <span className="text-gray-700">
                                        {item.item_name} × {item.quantity}
                                      </span>
                                      <span className="text-gray-900 font-medium">
                                        {formatCurrency((Number(item.unit_price_cents) * Number(item.quantity)) / 100)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {(plan.appliedDiscounts || []).length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Discounts</h4>
                                <div className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
                                  <span className="text-gray-700">
                                    {formatStackedDiscounts(plan.appliedDiscounts)}
                                  </span>
                                  <span className="text-gray-900 font-medium">
                                    -{formatCurrency(
                                      plan.appliedDiscounts.reduce(
                                        (sum, row) => sum + (Number(row.amount_cents) || 0),
                                        0
                                      ) / 100
                                    )}
                                  </span>
                                </div>
                                <div className="mt-1 space-y-0.5">
                                  {plan.appliedDiscounts.map((row) => (
                                    <div
                                      key={row.id || row.discount_id}
                                      className="flex justify-between px-3 text-xs text-gray-500"
                                    >
                                      <span>{row.title}</span>
                                      <span>-{formatCurrency((Number(row.amount_cents) || 0) / 100)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Payment Method */}
                            {plan.card_last_four && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <CreditCard className="w-5 h-5 text-gray-400" />
                                <div>
                                  <p className="text-sm font-medium">{plan.card_brand || 'Card'} ending in {plan.card_last_four}</p>
                                  <p className="text-xs text-gray-500">
                                    Expires {String(plan.card_expiry_month).padStart(2, '0')}/{String(plan.card_expiry_year).slice(-2)}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Upcoming Dates — active preview or paused owed cycles */}
                            {plan.status === 'active' && upcomingDates.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  <Calendar className="w-4 h-4" /> Upcoming Charges
                                </h4>
                                <div className="space-y-1">
                                  {upcomingDates.map((date) => (
                                    <div key={date} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
                                      <span className="text-gray-600">{formatChargeDate(date)}</span>
                                      <span className="text-gray-900 font-medium">{formatCurrency(plan.amount_cents / 100)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {plan.status === 'paused' && pausedUpcoming.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  <Calendar className="w-4 h-4" /> Upcoming Charges
                                </h4>
                                <div className="space-y-1">
                                  {pausedUpcoming.map((row) => (
                                    <div key={row.date} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 rounded text-sm">
                                      <span className="text-gray-600">
                                        {formatChargeDate(row.date)}
                                        {row.failed && (
                                          <span className="text-red-600"> (Failed)</span>
                                        )}
                                      </span>
                                      <span className="text-gray-900 font-medium">
                                        {formatCurrency((Number(row.amountCents) || 0) / 100)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                {(plan.openPeriods || []).length > 0 && (
                                  <p className="mt-2 text-xs text-gray-500">
                                    {(plan.openPeriods || []).length} missed · {formatCurrency(openTotalCents / 100)}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Charge History */}
                            {historyRows.length > 0 && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                  <Clock className="w-4 h-4" /> Charge History
                                </h4>
                                <div className="space-y-2">
                                  {historyRows.map((charge) => {
                                    const errorText = formatChargeHistoryError(charge);
                                    return (
                                    <div key={charge.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded text-sm">
                                      <div className="flex items-center gap-2">
                                        {CHARGE_STATUS_ICONS[charge.status] || CHARGE_STATUS_ICONS.pending}
                                        <div>
                                          <p className="text-gray-900">
                                            {formatChargeDate(charge.charge_date, 'MMM d, yyyy')}
                                            {charge.attempt_number > 1 && (
                                              <span className="text-xs text-gray-400 ml-1">(attempt {charge.attempt_number})</span>
                                            )}
                                          </p>
                                          {errorText && (
                                            <p className="text-xs text-red-500">{errorText}</p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <p className="font-medium">{formatCurrency(charge.amount_cents / 100)}</p>
                                        {charge.invoice_number && (
                                          <p className="text-xs text-gray-400">{charge.invoice_number}</p>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                                {historyTotal > HISTORY_PAGE_SIZE && (
                                  <div className="mt-2 flex items-center justify-center gap-2 text-xs text-gray-400">
                                    <button
                                      type="button"
                                      onClick={() => handleHistoryPageChange(historyPage - 1)}
                                      disabled={historyLoading || historyPage <= 1}
                                      aria-label="Previous charge history page"
                                      className="p-0.5 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-400"
                                    >
                                      <ChevronLeft className="h-3.5 w-3.5" />
                                    </button>
                                    <span>
                                      {historyLoading ? 'Loading…' : `${historyFrom}–${historyTo} of ${historyTotal}`}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleHistoryPageChange(historyPage + 1)}
                                      disabled={historyLoading || historyPage >= historyTotalPages}
                                      aria-label="Next charge history page"
                                      className="p-0.5 hover:text-gray-600 disabled:opacity-30 disabled:hover:text-gray-400"
                                    >
                                      <ChevronRight className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="border-t border-gray-200 pt-4 space-y-3">
                              {(plan.status === 'active' || plan.status === 'paused') && (
                                <PermissionGate permission={PERMISSIONS.recurring.edit}>
                                  <button
                                    onClick={() => setShowEditModal(true)}
                                    className="w-full px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover flex items-center justify-center gap-2"
                                  >
                                    <Pencil className="w-4 h-4" />
                                    Edit Plan
                                  </button>
                                </PermissionGate>
                              )}

                              <PermissionGate permission={PERMISSIONS.recurring.cancel}>
                                <div className="flex gap-3">
                                  {plan.status === 'active' && (
                                    <button
                                      onClick={handlePause}
                                      disabled={actionLoading}
                                      className="flex-1 px-4 py-2 border border-yellow-300 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-50 disabled:opacity-50"
                                    >
                                      Pause Plan
                                    </button>
                                  )}
                                  {plan.status === 'paused' && (
                                    <button
                                      onClick={() => setShowResumeModal(true)}
                                      disabled={actionLoading}
                                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                                    >
                                      Resume Plan
                                    </button>
                                  )}
                                  {(plan.status === 'active' || plan.status === 'paused') && (
                                    <button
                                      onClick={handleCancel}
                                      disabled={actionLoading}
                                      className="flex-1 px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                                    >
                                      Cancel Plan
                                    </button>
                                  )}
                                </div>
                              </PermissionGate>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>

          {/* ── CHILD MODALS ──
              All conditionally-mounted child Dialogs (EditRecurringPlanModal,
              ResumeRecurringPlanModal, and ConfirmActionModal below) MUST
              render inside this parent <Dialog> so Headless UI's nested-Dialog
              stack tracking via React context registers them. As siblings
              outside this <Dialog>, every click inside the child would fire
              the parent's outside-click detector. See LESSONS.md on Headless
              UI nested-Dialog stack tracking.
              If you add another child Dialog, place it here too. */}
          {showEditModal && (
            <EditRecurringPlanModal
              plan={plan}
              onClose={() => setShowEditModal(false)}
              onUpdated={async () => {
                setShowEditModal(false);
                try {
                  await reloadPlan();
                } catch (err) {
                  console.error('Failed to reload plan after edit:', err);
                  onPlanUpdated();
                }
              }}
            />
          )}

          {showResumeModal && plan && (
            <ResumeRecurringPlanModal
              plan={plan}
              onClose={() => setShowResumeModal(false)}
              onResumed={async () => {
                setShowResumeModal(false);
                try {
                  await reloadPlan();
                } catch (err) {
                  console.error('Failed to reload plan after resume:', err);
                  onPlanUpdated();
                }
              }}
            />
          )}

          {/* Confirm Action Modal — only mount when action pending to prevent flash on parent close */}
          {confirmModal.action && (
            <ConfirmActionModal
              open={confirmModal.open}
              onClose={() => setConfirmModal({ open: false, action: null })}
              onConfirm={handleConfirmAction}
              title={confirmModal.title}
              message={confirmModal.message}
              confirmText={confirmModal.confirmText}
              variant={confirmModal.variant}
            />
          )}
      </Dialog>
    </Transition.Root>
  );
}
